/**
 * FarmstandSection.tsx
 * ────────────────────
 * "This Isn't a Factory, It's a Farmstand" — cream (var(--color-brand-cream)) background section.
 * Comes directly after the dark-navy belief section.
 */

import React, { useRef, useState } from "react";
import { MapPin, CheckCircle2, Loader2, RotateCw } from "lucide-react";
import { motion } from "framer-motion";
import { FreshDeliveryAnimation } from "@/components/FreshDeliveryAnimation";
import { MediaPlaceholder, LpImage, LpVideo } from "@/components/LpMediaPlaceholder";
import { computeReceipt, type LpReceipt } from "@/lib/lp-express-pricing";
import { EditableText, EditableElement, VideoUrlEditor, RegenerateButton, LpImageActions, LinkUrlPopover, useLpMediaOverride, useLpEditor, useLpV2, type LpContentPath } from "./lp-editor";

const DEFAULT_MAPS_URL = "https://maps.google.com/?q=27725+Jefferson+Ave+STE+104+Temecula+CA+92590";

const CHECKOUT_URL = "https://thepapabear.com/apps/subscriptions/qc/1sQTsnfo";

const STAND_PHOTOS = [
  { src: "/farmstand-crowd.jpeg",     alt: "Customers lined up inside Papabear Farmstand", caption: "The Line Out the Door" },
  { src: "/farmstand-people.jpeg",    alt: "Customers inside the farmstand",                caption: "Full House"            },
  { src: "/farmstand-lil-bear.jpeg",  alt: "Little girl at the Lil Bear Farmstand",         caption: "Lil' Bear"            },
];

const FARM_PHOTOS = [
  { src: "/farm-cattle-sunlight.jpg",  alt: "Grass-fed cattle grazing in the sun",      caption: "Our Cattle"   },
  { src: "/farm-horses.jpg",           alt: "Ranch horses eating hay at golden hour",   caption: "Ranch Horses" },
  { src: "/farm-goats-fence.jpg",      alt: "Goats at the wooden fence",                caption: "The Herd"     },
  { src: "/farm-donkeys-goats.jpg",    alt: "Donkeys and goats grazing together",        caption: "Family Farm"  },
];

// Neutral, brand-agnostic fallbacks used only by Long Form v2 — same shape and
// count as v1 so the layout stays pixel-identical; only the brand-specific
// imagery paths and alt text are generalized.
const STAND_PHOTOS_V2 = [
  { src: "Farmstand crowd photo",   alt: "Customers lined up inside the farmstand", caption: "The Line Out the Door" },
  { src: "Farmstand interior photo", alt: "Customers inside the farmstand",          caption: "Full House"            },
  { src: "Farmstand visitor photo",  alt: "A young visitor at the farmstand",        caption: "Family Visit"          },
];

const FARM_PHOTOS_V2 = [
  { src: "Cattle photo",       alt: "Grass-fed cattle grazing in the sun",    caption: "Our Cattle"   },
  { src: "Ranch horses photo", alt: "Ranch horses at golden hour",            caption: "Ranch Horses" },
  { src: "Herd photo",         alt: "Animals at the wooden fence",            caption: "The Herd"     },
  { src: "Family farm photo",  alt: "Animals grazing together",               caption: "Family Farm"  },
];

const HOURS = [
  { day: "Monday",    hours: "8 AM – 5 PM" },
  { day: "Tuesday",   hours: "8 AM – 5 PM" },
  { day: "Wednesday", hours: "8 AM – 5 PM" },
  { day: "Thursday",  hours: "8 AM – 5 PM" },
  { day: "Friday",    hours: "8 AM – 5 PM" },
  { day: "Saturday",  hours: "8 AM – 5 PM" },
  { day: "Sunday",    hours: "Closed"       },
];

// v2 keeps the same 7-row layout (pixel-identical) but with neutral, brand-
// agnostic placeholder hours instead of Papabear's farmstand schedule. Real
// hours flow in per-row via originCopy.hours_rows when the brand provides them.
const HOURS_V2 = [
  { day: "Monday",    hours: "9 AM – 6 PM" },
  { day: "Tuesday",   hours: "9 AM – 6 PM" },
  { day: "Wednesday", hours: "9 AM – 6 PM" },
  { day: "Thursday",  hours: "9 AM – 6 PM" },
  { day: "Friday",    hours: "9 AM – 6 PM" },
  { day: "Saturday",  hours: "10 AM – 4 PM" },
  { day: "Sunday",    hours: "Closed"       },
];


// ── Inline farm header SVG ──
function FarmHeader() {
  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
      <svg viewBox="0 0 280 80" style={{ width: "100%", maxWidth: 320, overflow: "visible" }} fill="none">
        <rect x="10" y="40" width="260" height="38" fill="#D9CEAF" rx="2" />
        {/* Silo 1 */}
        <rect x="22" y="16" width="22" height="26" fill="#C4B99A" rx="1" />
        <ellipse cx="33" cy="16" rx="11" ry="6" fill="#B8AC8E" />
        {/* Silo 2 */}
        <rect x="60" y="22" width="18" height="20" fill="#C4B99A" rx="1" />
        <ellipse cx="69" cy="22" rx="9" ry="5" fill="#B8AC8E" />
        {/* Barn */}
        <rect x="100" y="20" width="80" height="58" fill="#A0896C" rx="2" />
        <polygon points="100,20 140,2 180,20" fill="#8B7355" />
        <rect x="126" y="42" width="28" height="36" fill="#6B5B4A" />
        <rect x="104" y="28" width="18" height="14" rx="1" fill="#C8A878" opacity="0.6" />
        <rect x="158" y="28" width="18" height="14" rx="1" fill="#C8A878" opacity="0.6" />
        {/* Weathervane */}
        <line x1="140" y1="2" x2="140" y2="-10" stroke="#6B5B4A" strokeWidth="1.5" />
        <polygon points="140,-10 148,-6 140,-4 132,-6" fill="#8B7355" />
        {/* Silo 3 */}
        <rect x="200" y="18" width="20" height="24" fill="#C4B99A" rx="1" />
        <ellipse cx="210" cy="18" rx="10" ry="5" fill="#B8AC8E" />
        {/* Fence */}
        {[244, 252, 260, 268].map(x => <rect key={x} x={x} y="52" width="2" height="14" fill="#A0896C" />)}
        <line x1="244" y1="56" x2="270" y2="56" stroke="#A0896C" strokeWidth="1" />
        <line x1="244" y1="61" x2="270" y2="61" stroke="#A0896C" strokeWidth="1" />
        {/* Windows lit */}
        <rect x="18" y="52" width="12" height="14" fill="#D4660A" opacity="0.7" rx="1" />
        <rect x="188" y="52" width="12" height="14" fill="#a3c463" opacity="0.6" rx="1" />
      </svg>
    </div>
  );
}

// A single Farmstand still-image slot. When an AI-generated image is ready it
// renders the durable R2 image; while still generating it shows a spinner; and
// when absent/failed it falls back to the dashed MediaPlaceholder.
type OriginImageSlot = {
  key?: string;
  imageUrl?: string;
  altText?: string;
  imageLoading?: boolean;
  imageStatus?: string;
};

// Spinner box matching MediaPlaceholder's footprint, shown while a slot's
// image is still being generated.
function OriginImageLoader({ style, rounded = false }: { style?: React.CSSProperties; rounded?: boolean }) {
  return (
    <div
      data-testid="loading-origin-image"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        minHeight: 80,
        backgroundColor: "#E8E2D6",
        borderRadius: rounded ? 10 : 0,
        boxSizing: "border-box",
        ...style,
      }}
    >
      <Loader2 className="animate-spin" style={{ width: 26, height: 26, color: "#6B6560", opacity: 0.55 }} />
    </div>
  );
}

// Retry affordance box (matching MediaPlaceholder's footprint) shown when a
// slot's image generation failed AND a retry handler is available (in-app
// builder only). Clicking regenerates only this one slot.
function OriginImageRetry({
  slotKey,
  onRetry,
  style,
  rounded = false,
}: {
  slotKey: string;
  onRetry: (slotKey: string) => void;
  style?: React.CSSProperties;
  rounded?: boolean;
}) {
  const [clicked, setClicked] = useState(false);
  return (
    <div
      data-testid={`retry-origin-${slotKey}`}
      style={{
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        width: "100%",
        minHeight: 80,
        backgroundColor: "#EFE9DC",
        border: "1px dashed #C7BCA3",
        borderRadius: rounded ? 10 : 0,
        boxSizing: "border-box",
        padding: 12,
        ...style,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 700, color: "#8A7F68", textAlign: "center" as const }}>
        Image failed to generate
      </span>
      <button
        type="button"
        disabled={clicked}
        onClick={() => {
          setClicked(true);
          onRetry(slotKey);
        }}
        data-testid={`button-retry-origin-${slotKey}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          borderRadius: 999,
          border: "1px solid var(--color-brand-navyaccent)",
          backgroundColor: "#FFFFFF",
          color: "var(--color-brand-navyaccent)",
          fontSize: 12,
          fontWeight: 700,
          cursor: clicked ? "default" : "pointer",
          opacity: clicked ? 0.6 : 1,
        }}
      >
        <RotateCw style={{ width: 13, height: 13 }} className={clicked ? "animate-spin" : ""} />
        {clicked ? "Retrying…" : "Retry"}
      </button>
    </div>
  );
}

// Renders an origin image slot: durable image → loading spinner → retry (on
// failure, in-app only) → placeholder.
function OriginMedia({
  slot,
  fallbackPath,
  alt,
  rounded = false,
  style,
  onRetry,
  overrideKey,
  regenKey,
  noActions = false,
}: {
  slot?: OriginImageSlot;
  fallbackPath: string;
  alt?: string;
  rounded?: boolean;
  style?: React.CSSProperties;
  onRetry?: (slotKey: string) => void;
  /** Stable media-override key for this slot (e.g. "originImages:farmstand-exterior").
   *  Decoupled from slot.key so a static-fallback slot is still uploadable. */
  overrideKey?: string;
  /** AI slot key used to regenerate (only AI slots support regeneration). */
  regenKey?: string;
  /** Suppress the upload/regenerate overlay. Required when this media is itself
   *  rendered inside a <button> (e.g. carousel thumbnails) — nesting the
   *  action <button>s inside another <button> is invalid DOM and breaks the
   *  primary image's regenerate handler via an invalid-hook-call cascade. */
  noActions?: boolean;
}) {
  const overrideUrl = useLpMediaOverride(overrideKey);
  const displaySrc = overrideUrl || slot?.imageUrl;
  const canRegen = !!regenKey && !!onRetry && !!slot?.imageUrl;
  const actions = overrideKey && !noActions ? (
    <LpImageActions
      slotKey={overrideKey}
      canRegenerate={canRegen}
      onRegenerate={regenKey && onRetry ? () => onRetry(regenKey) : undefined}
      testIdPrefix={overrideKey.replace(/[^a-zA-Z0-9_-]/g, "-")}
    />
  ) : null;
  let body: React.ReactNode;
  if (displaySrc) {
    body = (
      <img
        src={displaySrc}
        alt={slot?.altText || alt || ""}
        data-testid={`img-origin-${slot?.key || fallbackPath}`}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: rounded ? 10 : 0,
        }}
      />
    );
  } else if (slot?.imageLoading) {
    body = <OriginImageLoader rounded={rounded} style={style} />;
  } else if (onRetry && slot?.key && slot?.imageStatus === "failed") {
    body = <OriginImageRetry slotKey={slot.key} onRetry={onRetry} rounded={rounded} style={style} />;
  } else {
    body = <MediaPlaceholder path={fallbackPath} rounded={rounded} style={style} />;
  }
  return (
    <div
      className="relative group/lpimg"
      style={{ width: "100%", height: "100%", borderRadius: rounded ? 10 : 0, overflow: "hidden", ...style }}
    >
      {body}
      {actions}
    </div>
  );
}

// ── Simple touch-swipeable photo carousel (AI-generated images w/ fallback) ──
type CarouselPhoto = { src: string; alt: string; caption: string; slot?: OriginImageSlot; captionPath?: LpContentPath; captionRaw?: string; captionFallback?: string };
function PhotoCarousel({ photos, onRetry }: { photos: CarouselPhoto[]; onRetry?: (slotKey: string) => void }) {
  const [idx, setIdx] = useState(0);
  const touchRef = useRef<number | null>(null);
  const go = (d: number) => setIdx(prev => (prev + d + photos.length) % photos.length);

  return (
    <div>
      <div style={{ position: "relative" as const }}>
        <div
          style={{ borderRadius: 14, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.15)", aspectRatio: "4/3" }}
          onTouchStart={e => { touchRef.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            if (touchRef.current === null) return;
            const diff = e.changedTouches[0].clientX - touchRef.current;
            if (Math.abs(diff) > 40) go(diff < 0 ? 1 : -1);
            touchRef.current = null;
          }}
        >
          <OriginMedia slot={photos[idx].slot} fallbackPath={photos[idx].src} alt={photos[idx].alt} rounded={false} style={{ height: "100%" }} onRetry={onRetry} overrideKey={`originImages:${photos[idx].slot?.key || photos[idx].src}`} regenKey={photos[idx].slot?.key} />
        </div>
        {(["‹", "›"] as const).map((arrow, ai) => (
          <button key={ai} onClick={() => go(ai === 0 ? -1 : 1)} aria-label={ai === 0 ? "Previous" : "Next"}
            style={{ position: "absolute" as const, top: "calc(50% - 18px)", [ai === 0 ? "left" : "right"]: 10, width: 36, height: 36, borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.85)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", fontSize: 20, color: "var(--color-brand-deepnavy)", fontWeight: 700 }}>
            {arrow}
          </button>
        ))}
      </div>
      <p style={{ textAlign: "center" as const, fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase" as const, color: "#6B6560", marginTop: 10, marginBottom: 10 }}>
        {photos[idx].captionPath ? (
          <EditableText
            as="span"
            path={photos[idx].captionPath!}
            value={photos[idx].captionRaw}
            placeholder={photos[idx].captionFallback}
          />
        ) : (
          photos[idx].caption
        )}
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
        {photos.map((photo, i) => (
          <button key={i} onClick={() => setIdx(i)} aria-label={`Photo ${i + 1}`}
            style={{ width: 56, height: 42, borderRadius: 6, overflow: "hidden", padding: 0, border: idx === i ? "2px solid var(--color-brand-navyaccent)" : "2px solid transparent", opacity: idx === i ? 1 : 0.5, cursor: "pointer", transition: "all 0.2s ease" }}>
            <OriginMedia slot={photo.slot} fallbackPath={photo.src} alt={photo.alt} rounded={false} style={{ height: "100%", minHeight: 0 }} overrideKey={`originImages:${photo.slot?.key || photo.src}`} regenKey={photo.slot?.key} noActions />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Minimal farm scene SVG ──
function FarmSceneSVG() {
  return (
    <svg width="100%" height="48" viewBox="0 0 360 48" preserveAspectRatio="xMidYMax meet" style={{ display: "block" }}>
      <rect x="0" y="44" width="360" height="4" rx="2" fill="#D8CFC3" />
      <polygon points="20,44 20,28 35,18 50,28 50,44" fill="#A0896C" />
      <polygon points="20,28 35,18 50,28" fill="#8B7355" />
      <rect x="31" y="34" width="8" height="10" fill="#6B5B4A" />
      {[62, 78, 94, 110, 126].map(x => <rect key={x} x={x} y="34" width="2" height="10" fill="#A0896C" />)}
      <line x1="62" y1="36" x2="128" y2="36" stroke="#A0896C" strokeWidth="1" />
      <line x1="62" y1="40" x2="128" y2="40" stroke="#A0896C" strokeWidth="1" />
      <rect x="300" y="24" width="3" height="20" fill="#8B7355" />
      <circle cx="301" cy="20" r="10" fill="#8BA888" opacity="0.7" />
    </svg>
  );
}

type FarmstandOriginCopy = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  fresh_badge?: string;
  block_1_headline?: string;
  block_1_body?: string;
  block_2_headline?: string;
  block_2_body?: string;
  block_3_headline?: string;
  block_3_body?: string;
  location_carousel_captions?: string[];
  origin_carousel_captions?: string[];
};

// Inline spinner shown in place of an AI copy field while the lp-origin-copy /
// lp-quote-copy workflows are still generating — never flash the static
// fallback text in the meantime.
function CopyLoader() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "100%", padding: "4px 0" }} data-testid="loading-farmstand-copy">
      <Loader2 className="animate-spin" style={{ width: 20, height: 20, color: "var(--color-brand-deepnavy)", opacity: 0.45 }} />
    </span>
  );
}

export function FarmstandSection({
  receipt,
  bundleImage,
  bundleProductPhotos,
  brandName,
  originCopy,
  originImages,
  originImagesLoading = false,
  quotes,
  originLoading = false,
  quotesLoading = false,
  videoMakingUrl,
  videoLifestyleUrl,
  videoMakingOverride,
  videoLifestyleOverride,
  videosLoading = false,
  checkoutUrl,
  onRetrySlot,
}: {
  receipt?: LpReceipt;
  // Real product flat-lay image; falls back to the dashed placeholder.
  bundleImage?: string | null;
  // Full product photo gallery, backing the "choose from product photos"
  // picker on the order-ticket image.
  bundleProductPhotos?: string[];
  // Real brand name; falls back to the hardcoded "Papabear" identity literals
  // (door sign + order ticket).
  brandName?: string | null;
  // Brand's own (R2-mirrored) Instagram videos. When present they replace the
  // hardcoded /farm-video-*.mp4 fallbacks:
  //   videoMakingUrl    → "See Us Make It From Scratch" slot
  //   videoLifestyleUrl → "Life Around the Farm" slot
  videoMakingUrl?: string | null;
  videoLifestyleUrl?: string | null;
  // Per-page video URL overrides (set in the in-canvas editor). When present
  // they win over the brand video for that slot. Stored on the page's
  // generatedContent — never on the brand record.
  videoMakingOverride?: string | null;
  videoLifestyleOverride?: string | null;
  // True while the brand's Instagram videos are being fetched/mirrored. While
  // true, a slot with no durable URL yet shows a loading spinner instead of the
  // dashed TODO placeholder.
  videosLoading?: boolean;
  originCopy?: FarmstandOriginCopy;
  // AI-generated Farmstand still images keyed by slot id. Missing/failed slots
  // fall back to the dashed MediaPlaceholder; imageLoading drives the spinner.
  originImages?: OriginImageSlot[];
  // True for the whole origin-image pipeline (copy → image gen → poll). While
  // true, every slot still missing a durable URL shows a spinner instead of the
  // hardcoded static placeholder — so we never flash the old TODO boxes mid-run.
  originImagesLoading?: boolean;
  quotes?: Record<string, { quote?: string; attribution?: string }>;
  // True while the origin-copy / quote workflows are in flight; drives the
  // per-field loading spinners instead of showing the hardcoded fallback.
  originLoading?: boolean;
  quotesLoading?: boolean;
  // Real brand checkout URL. Only consulted in Long Form v2 — v1 keeps the
  // template's hardcoded CHECKOUT_URL so its rendered output stays byte-identical.
  checkoutUrl?: string | null;
  // Retry a single failed origin image slot (in-app builder only). Undefined on
  // the public/published page so failed slots stay empty (no retry affordance).
  onRetrySlot?: (mergeKey: "originImages", slotKey: string) => void;
} = {}) {
  const v2 = useLpV2();
  const r: LpReceipt = receipt ?? computeReceipt([], undefined, null);
  const editor = useLpEditor();
  const mapsUrl = ((originCopy as any)?.maps_url ?? (v2 ? "" : DEFAULT_MAPS_URL)) as string;
  // v2 never fabricates farmstand hours/address. Only show the door-sign card
  // when the brand actually supplies real hours rows or a real address; v1 keeps
  // its original always-on behavior.
  const doorSignHasRealHours =
    Array.isArray((originCopy as any)?.hours_rows) &&
    (originCopy as any).hours_rows.some(
      (row: any) => row?.day?.trim?.() || row?.hours?.trim?.(),
    );
  const doorSignHasRealAddress = !!(
    (originCopy as any)?.address_headline?.trim?.() ||
    (originCopy as any)?.address_line_1?.trim?.() ||
    (originCopy as any)?.address_line_2?.trim?.()
  );
  const showDoorSign = !v2 || doorSignHasRealHours || doorSignHasRealAddress;
  const orderTicketImage = useLpMediaOverride("product:farmstand-cta") ?? bundleImage;
  const retryOrigin = onRetrySlot ? (slotKey: string) => onRetrySlot("originImages", slotKey) : undefined;
  // Resolve an image slot by key. If it already has a durable URL we use it as
  // is; otherwise, while the pipeline is still loading, force the spinner so the
  // static placeholder never shows mid-generation.
  const resolveSlot = (key: string): OriginImageSlot | undefined => {
    const s = (originImages ?? []).find((x) => x?.key === key);
    if (s?.imageUrl) return s;
    // This slot's own in-flight state always spins.
    if (s?.imageLoading || s?.imageStatus === "pending") {
      return { ...s, key, imageLoading: true };
    }
    // Pre-slot window (copy still generating, slot not created yet): spin so the
    // static placeholder never flashes. A slot that already exists but failed is
    // NOT forced to spin here — it keeps its "failed" status so a single-slot
    // retry doesn't make sibling failed slots appear to regenerate too.
    if (originImagesLoading && !s) {
      return { key, imageLoading: true };
    }
    return s;
  };
  // The "reframe" pull-quote wins the section headline; then the AI section
  // headline; then the hardcoded JSX. craft_statement becomes a hang-tag on
  // the buy box (only rendered when present).
  const reframeQuote = quotes?.reframe?.quote?.trim() || "";
  const aiHeadline = originCopy?.section_headline?.trim() || "";
  const headline = reframeQuote || aiHeadline;
  // The headline can come from either workflow (reframe quote wins, else AI
  // section headline), so it's "loading" until either source has resolved.
  const headlineLoading = !headline && (originLoading || quotesLoading);
  const craftStatement = quotes?.craft_statement?.quote?.trim() || "";
  const eyebrow = originCopy?.section_eyebrow?.trim() || "";
  const subheadline = originCopy?.section_subheadline?.trim() || "";
  const freshBadgeVal = originCopy?.fresh_badge?.trim() || "";
  // Effective video sources: a per-page override (set in the in-canvas editor)
  // wins over the brand's mirrored Instagram video; LpVideo falls back to the
  // hardcoded /farm-video-*.mp4 when both are empty.
  const makingSrc = (videoMakingOverride?.trim() || videoMakingUrl) ?? null;
  const lifestyleSrc = (videoLifestyleOverride?.trim() || videoLifestyleUrl) ?? null;
  // Captions fall back per-slot to the hardcoded ones; each photo also carries
  // its AI-generated image slot (keyed stand-<i> / farm-<i>) when present.
  const standPhotos: CarouselPhoto[] = (v2 ? STAND_PHOTOS_V2 : STAND_PHOTOS).map((p, i) => ({
    ...p,
    caption: originCopy?.location_carousel_captions?.[i]?.trim() || p.caption,
    captionPath: ["originCopy", "location_carousel_captions", i],
    captionRaw: originCopy?.location_carousel_captions?.[i]?.trim() || "",
    captionFallback: p.caption,
    slot: resolveSlot(`stand-${i}`),
  }));
  const farmPhotos: CarouselPhoto[] = (v2 ? FARM_PHOTOS_V2 : FARM_PHOTOS).map((p, i) => ({
    ...p,
    caption: originCopy?.origin_carousel_captions?.[i]?.trim() || p.caption,
    captionPath: ["originCopy", "origin_carousel_captions", i],
    captionRaw: originCopy?.origin_carousel_captions?.[i]?.trim() || "",
    captionFallback: p.caption,
    slot: resolveSlot(`farm-${i}`),
  }));
  return (
    <section id="farmstand-section" className="pt-20 pb-8 px-4 sm:px-6" style={{ backgroundColor: "var(--color-brand-cream)" }}>
      <div className="max-w-[520px] mx-auto">

        {/* Farm illustration + headline */}
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6 }}>
          <FarmHeader />
        </motion.div>

        <motion.div className="mb-4 text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6 }}>
          {eyebrow && (
            <EditableText
              as="p"
              path={["originCopy", "section_eyebrow"]}
              elementKey="farmstand:section_eyebrow"
              value={eyebrow}
              placeholder=""
              style={{ fontSize: 10, fontWeight: 900, letterSpacing: "0.22em", color: "var(--color-brand-greenaccent)", textTransform: "uppercase" as const, marginBottom: 8 }}
              testId="text-farmstand-eyebrow"
            />
          )}
          <h2 className="font-black text-3xl leading-tight" style={{ color: "var(--color-brand-deepnavy)" }} data-testid="text-farmstand-headline">
            {headline ? (
              <EditableText as="span" path={["originCopy", "section_headline"]} value={headline} placeholder="" multiline />
            ) : headlineLoading ? <CopyLoader /> : (
              <EditableText as="span" path={["originCopy", "section_headline"]} value="" placeholder={v2 ? "This isn't a faceless operation. It's the real thing." : "This isn't a factory. It's a farmstand."} multiline />
            )}
          </h2>
          {subheadline && (
            <EditableText
              as="p"
              path={["originCopy", "section_subheadline"]}
              elementKey="farmstand:section_subheadline"
              value={subheadline}
              placeholder=""
              multiline
              style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6, color: "#6B6560" }}
              testId="text-farmstand-subheadline"
            />
          )}
        </motion.div>

        {/* "Made fresh daily" badge */}
        <motion.div className="mb-5 text-center" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.5 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--color-brand-greenaccent)", color: "white", fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" as const, padding: "6px 14px", borderRadius: 999 }} data-testid="text-farmstand-fresh-badge">
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#A0F0C0", display: "inline-block" }} />
            {originLoading && !freshBadgeVal ? (
              <Loader2 className="animate-spin" style={{ width: 13, height: 13, color: "white" }} />
            ) : (
              <EditableText as="span" path={["originCopy", "fresh_badge"]} value={freshBadgeVal} placeholder={v2 ? "Made fresh daily" : "Skincare made fresh daily"} />
            )}
          </span>
        </motion.div>

        {/* Farmstand exterior */}
        <EditableElement elementKey="farmstand:exterior-image" toolbarSide="left" testId="element-farmstand-exterior">
        <motion.div className="w-full rounded-2xl mb-10 overflow-hidden" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }} initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
          <OriginMedia slot={resolveSlot("farmstand-exterior")} fallbackPath={v2 ? "Farmstand exterior photo" : "/farmstand-exterior.jpg"} alt="Farmstand exterior" rounded={false} style={{ height: 320 }} onRetry={retryOrigin} overrideKey="originImages:farmstand-exterior" regenKey="farmstand-exterior" />
        </motion.div>
        </EditableElement>

        {/* "We have a front door" copy */}
        <motion.div className="mb-6" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.5 }}>
          <h3 className="font-black text-2xl leading-tight mb-2" style={{ color: "var(--color-brand-deepnavy)" }} data-testid="text-farmstand-block-1-headline">
            {originLoading && !originCopy?.block_1_headline?.trim()
              ? <CopyLoader />
              : <EditableText as="span" elementKey="farmstand:block_1_headline" path={["originCopy", "block_1_headline"]} value={originCopy?.block_1_headline?.trim() || ""} placeholder="We have a front door. You can walk through it." multiline />}
          </h3>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--color-brand-deepnavy)", fontWeight: 700 }} data-testid="text-farmstand-block-1-body">
            {originLoading && !originCopy?.block_1_body?.trim()
              ? <CopyLoader />
              : <EditableText as="span" elementKey="farmstand:block_1_body" path={["originCopy", "block_1_body"]} value={originCopy?.block_1_body?.trim() || ""} placeholder={v2 ? "Real place, real people. Walk in, meet the team, and try it for yourself." : "Real farmstand. Temecula, California. Walk in, meet the family, try the products."} multiline />}
          </p>
          <EditableText
            as="p"
            path={["originCopy", "ship_note"]}
            value={(originCopy as any)?.ship_note || ""}
            placeholder="Can't visit? We'll ship it fresh to your door. Every order is made to order and shipped the same week."
            multiline
            style={{ fontSize: 13, lineHeight: 1.5, color: "#6B6560", marginTop: 8 }}
            testId="text-farmstand-ship-note"
          />
        </motion.div>

        {/* Farmstand photo carousel */}
        <motion.div className="mb-6" initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
          <PhotoCarousel photos={standPhotos} onRetry={retryOrigin} />
        </motion.div>

        {/* Door sign — whole card is one hide/delete unit. In v2, hidden unless the
            brand supplies real hours or a real address (never fabricated hours). */}
        {showDoorSign && (
        <EditableElement elementKey="farmstand:door-sign-card" testId="element-farmstand-door-sign">
        <motion.div className="mb-8 flex justify-center" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: 0.1 }}>
          <div className="w-full bg-white" style={{ border: "3px solid var(--color-brand-deepnavy)", boxShadow: "4px 4px 0px var(--color-brand-deepnavy)", transform: "rotate(-0.5deg)" }}>
            <div className="py-3 px-5 text-center" style={{ borderBottom: "3px solid var(--color-brand-deepnavy)", backgroundColor: "var(--color-brand-deepnavy)" }}>
              <p className="font-black uppercase tracking-[0.3em] text-white" style={{ fontSize: "13px" }}>{v2 ? (brandName?.trim() || "Visit Us") : (brandName?.trim() ? `${brandName.trim()}'s Farmstand` : "Papabear's Farmstand")}</p>
            </div>
            <div className="py-2 text-center" style={{ borderBottom: "2px solid var(--color-brand-deepnavy)", backgroundColor: "var(--color-brand-green)" }}>
              <EditableText as="p" formatOnly path={["originCopy", "open_status_label"]} value={(originCopy as any)?.open_status_label ?? "● We're Open"} placeholder="● We're Open" className="font-black uppercase tracking-[0.4em] text-white text-xs" />
            </div>
            <div className="px-5 py-3">
              <EditableText as="p" formatOnly path={["originCopy", "hours_label"]} value={(originCopy as any)?.hours_label ?? "Hours of Operation"} placeholder="Hours of Operation" className="font-black uppercase tracking-[0.25em] text-center mb-3" style={{ color: "var(--color-brand-deepnavy)", fontSize: "10px", borderBottom: "1px solid var(--color-brand-deepnavy)", paddingBottom: "8px" }} />
              {(v2 ? HOURS_V2 : HOURS).map(({ day, hours }, i) => (
                <div key={day} className="flex items-center justify-between py-1.5" style={{ borderBottom: i < 6 ? "1px dashed #D8CFC3" : "none" }}>
                  <EditableText as="span" formatOnly path={["originCopy", "hours_rows", i, "day"]} value={(originCopy as any)?.hours_rows?.[i]?.day ?? day} placeholder={day} className="text-sm font-medium" style={{ color: "var(--color-brand-deepnavy)" }} />
                  <EditableText as="span" formatOnly path={["originCopy", "hours_rows", i, "hours"]} value={(originCopy as any)?.hours_rows?.[i]?.hours ?? hours} placeholder={hours} className="text-sm font-black" style={{ color: hours === "Closed" ? "#D64040" : "var(--color-brand-green)" }} />
                </div>
              ))}
            </div>
            <div className="group/lplink relative">
              {editor?.onEditLinkUrl && (
                <LinkUrlPopover path={["originCopy", "maps_url"]} url={mapsUrl} testId="farmstand-maps" />
              )}
            <a
              href={mapsUrl || undefined}
              target={editor ? undefined : mapsUrl ? "_blank" : undefined}
              rel={editor ? undefined : mapsUrl ? "noopener noreferrer" : undefined}
              onClick={editor ? (e) => e.preventDefault() : undefined}
              className="block"
              style={{ borderTop: "3px solid var(--color-brand-deepnavy)" }}
            >
              <div className="px-5 py-5 text-center" style={{ backgroundColor: "var(--color-brand-cream)" }}>
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-3" style={{ backgroundColor: "var(--color-brand-deepnavy)" }}>
                  <MapPin className="w-5 h-5" style={{ color: "var(--color-brand-coral)" }} strokeWidth={2.5} />
                </div>
                <EditableText as="p" noHideDelete path={["originCopy", "address_headline"]} value={(originCopy as any)?.address_headline || ""} placeholder="We have a real address." className="font-black text-sm mb-0.5" style={{ color: "var(--color-brand-deepnavy)" }} />
                <EditableText as="p" noHideDelete path={["originCopy", "address_subhead"]} value={(originCopy as any)?.address_subhead || ""} placeholder="Come say hi." className="font-black text-sm mb-3" style={{ color: "var(--color-brand-coral)" }} />
                <EditableText as="p" noHideDelete path={["originCopy", "address_line_1"]} value={(originCopy as any)?.address_line_1 || ""} placeholder={v2 ? "123 Main Street" : "27725 Jefferson Ave STE 104"} className="font-semibold text-sm leading-snug" style={{ color: "var(--color-brand-deepnavy)" }} />
                <EditableText as="p" noHideDelete path={["originCopy", "address_line_2"]} value={(originCopy as any)?.address_line_2 || ""} placeholder={v2 ? "Your City, State ZIP" : "Temecula, CA 92590"} className="font-semibold text-sm leading-snug mb-2" style={{ color: "var(--color-brand-deepnavy)" }} />
                <EditableText as="p" noHideDelete path={["originCopy", "maps_cta_label"]} value={(originCopy as any)?.maps_cta_label || ""} placeholder="Tap to open in Maps →" className="text-[11px] font-medium" style={{ color: "#6B6560" }} />
              </div>
            </a>
            </div>
          </div>
        </motion.div>
        </EditableElement>
        )}

        {/* "Small batch. By hand." card — whole card is one hide/delete unit */}
        <motion.div className="mb-8" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.5 }}>
          <EditableElement elementKey="farmstand:small-batch-card" testId="element-farmstand-small-batch">
          <div className="rounded-2xl overflow-hidden mb-4" style={{ backgroundColor: "var(--color-brand-card)", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
            <div className="px-5 pt-5 pb-4">
              <h3 className="font-black leading-none mb-4" style={{ color: "var(--color-brand-navyaccent)", fontSize: "clamp(28px, 8vw, 38px)", letterSpacing: "-0.02em" }} data-testid="text-farmstand-block-2-headline">
                {originLoading && !originCopy?.block_2_headline?.trim()
                  ? <CopyLoader />
                  : <EditableText as="span" formatOnly elementKey="farmstand:block_2_headline" path={["originCopy", "block_2_headline"]} value={originCopy?.block_2_headline?.trim() || ""} placeholder="Small batch. By hand. Every single time." multiline />}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "#6B6560" }} data-testid="text-farmstand-block-2-body">
                {originLoading && !originCopy?.block_2_body?.trim()
                  ? <CopyLoader />
                  : <EditableText as="span" formatOnly elementKey="farmstand:block_2_body" path={["originCopy", "block_2_body"]} value={originCopy?.block_2_body?.trim() || ""} placeholder="No assembly line. No conveyor belt. Small batches, by hand. When your order ships, it was probably made that same week." multiline />}
              </p>
            </div>
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", background: "linear-gradient(90deg, var(--color-brand-navyaccent) 0%, #1E3F73 50%, var(--color-brand-navyaccent) 100%)", padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, borderRadius: "0 0 16px 16px" }}>
              <span style={{ color: "#C41E3A", fontSize: 14 }}>★</span>
              <EditableText as="span" formatOnly path={["originCopy", "made_in_america_label"]} value={(originCopy as any)?.made_in_america_label ?? (v2 ? "Crafted With Pride" : "Proudly Made in America")} placeholder={v2 ? "Crafted With Pride" : "Proudly Made in America"} style={{ fontSize: 12, letterSpacing: "0.14em", fontWeight: 900, color: "#FFFFFF", textTransform: "uppercase" as const }} />
              <span style={{ color: "#C41E3A", fontSize: 14 }}>★</span>
            </div>
          </div>
          </EditableElement>

          {/* Video 2 — "making" slot */}
          <EditableElement elementKey="farmstand:video:making" testId="element-farmstand-video-making">
          <motion.div className="my-6" initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
            <div style={{ textAlign: "center" as const, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, #D8CFC3)" }} />
                <EditableText as="span" elementKey="farmstand:video_2_eyebrow" path={["originCopy", "video_2_eyebrow"]} value={(originCopy as any)?.video_2_eyebrow ?? (v2 ? "Behind the Scenes" : "From Our Ranch")} placeholder={v2 ? "Behind the Scenes" : "From Our Ranch"} style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.2em", color: "#A0896C", textTransform: "uppercase" as const }} />
                <div style={{ flex: 1, height: 1, background: "linear-gradient(270deg, transparent, #D8CFC3)" }} />
              </div>
              <EditableText as="h3" multiline elementKey="farmstand:video_2_headline" path={["originCopy", "video_2_headline"]} value={(originCopy as any)?.video_2_headline ?? "See Us Make It From Scratch"} placeholder="See Us Make It From Scratch" className="font-black" style={{ fontSize: "clamp(22px, 6vw, 28px)", color: "var(--color-brand-navyaccent)", lineHeight: 1.1, margin: 0 }} />
            </div>
            <div className="relative mx-auto max-w-full md:max-w-[320px]">
              <VideoUrlEditor slot="making" currentOverride={videoMakingOverride} />
              <div className="rounded-2xl overflow-hidden" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.15)", border: "3px solid var(--color-brand-navyaccent)" }}>
                <LpVideo src={makingSrc} path="/farm-video-2.mp4" rounded={false} loading={videosLoading} style={{ aspectRatio: "9/16" }} />
              </div>
            </div>
            <EditableText as="p" multiline elementKey="farmstand:video_2_caption" path={["originCopy", "video_2_caption"]} value={(originCopy as any)?.video_2_caption ?? (v2 ? "See how we make it, step by step." : "See how we make it, straight from the ranch.")} placeholder={v2 ? "See how we make it, step by step." : "See how we make it, straight from the ranch."} className="text-center text-xs mt-3 italic" style={{ color: "#6B6560" }} />
          </motion.div>
          </EditableElement>

          <EditableElement elementKey="farmstand:shipping-badge" testId="element-farmstand-shipping-badge">
            <FreshDeliveryAnimation />
          </EditableElement>
        </motion.div>

        {/* Farmstand interior */}
        <EditableElement elementKey="farmstand:interior-image" toolbarSide="left" testId="element-farmstand-interior">
        <motion.div className="w-full rounded-2xl mb-10 overflow-hidden" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }} initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
          <OriginMedia slot={resolveSlot("farmstand-interior")} fallbackPath={v2 ? "Farmstand interior photo" : "/farmstand-interior-display.jpg"} alt="Farmstand interior display" rounded={false} style={{ height: 280 }} onRetry={retryOrigin} overrideKey="originImages:farmstand-interior" regenKey="farmstand-interior" />
        </motion.div>
        </EditableElement>

        {/* "We're a family" copy */}
        <motion.div className="mb-10" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.5 }}>
          <h3 className="font-black text-2xl leading-tight mb-3" style={{ color: "var(--color-brand-deepnavy)" }} data-testid="text-farmstand-block-3-headline">
            {originLoading && !originCopy?.block_3_headline?.trim()
              ? <CopyLoader />
              : <EditableText as="span" elementKey="farmstand:block_3_headline" path={["originCopy", "block_3_headline"]} value={originCopy?.block_3_headline?.trim() || ""} placeholder="We're not a brand. We're a family." multiline />}
          </h3>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: "#6B6560" }} data-testid="text-farmstand-block-3-body">
            {originLoading && !originCopy?.block_3_body?.trim()
              ? <CopyLoader />
              : <EditableText as="span" elementKey="farmstand:block_3_body" path={["originCopy", "block_3_body"]} value={originCopy?.block_3_body?.trim() || ""} placeholder={v2 ? "We source the finest materials and make every product by hand. The same people who craft your order answer your emails and say hello when you stop by." : "We raise cattle, source the finest tallow, and make every product by hand. The same people who craft your skincare answer your emails and wave hello from the farmstand."} multiline />}
          </p>
        </motion.div>

        {/* Ranch animal photo carousel */}
        <motion.div className="mb-12" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.55 }}>
          <EditableText as="p" path={["originCopy", "ranch_location_label"]} value={(originCopy as any)?.ranch_location_label ?? (v2 ? "Where it's made" : "From our ranch in Murrieta, CA")} placeholder={v2 ? "Where it's made" : "From our ranch in Murrieta, CA"} className="text-[10px] font-black uppercase tracking-[0.22em] mb-2" style={{ color: "#6B6560" }} />
          <div style={{ marginBottom: 10 }}><FarmSceneSVG /></div>
          <PhotoCarousel photos={farmPhotos} onRetry={retryOrigin} />
        </motion.div>

        {/* Farm video 1 — "lifestyle" slot */}
        <EditableElement elementKey="farmstand:video:lifestyle" testId="element-farmstand-video-lifestyle">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <EditableText as="h3" elementKey="farmstand:video_1_headline" path={["originCopy", "video_1_headline"]} value={(originCopy as any)?.video_1_headline ?? (v2 ? "Behind the Scenes" : "Life Around the Farm")} placeholder={v2 ? "Behind the Scenes" : "Life Around the Farm"} className="font-black text-lg uppercase tracking-wider text-center mb-4" style={{ color: "var(--color-brand-deepnavy)" }} />
          <div className="relative mx-auto max-w-full md:max-w-[320px]">
            <VideoUrlEditor slot="lifestyle" currentOverride={videoLifestyleOverride} />
            <div style={{ borderRadius: 16, overflow: "hidden", border: "3px solid var(--color-brand-deepnavy)", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
              <LpVideo src={lifestyleSrc} path="/farm-video-1.mp4" rounded={false} loading={videosLoading} style={{ aspectRatio: "9/16" }} />
            </div>
          </div>
          <EditableText as="p" multiline elementKey="farmstand:video_1_caption" path={["originCopy", "video_1_caption"]} value={(originCopy as any)?.video_1_caption ?? (v2 ? "A peek behind the scenes where your products are made." : "A peek at the ranch where your skincare is made.")} placeholder={v2 ? "A peek behind the scenes where your products are made." : "A peek at the ranch where your skincare is made."} className="text-center text-xs mt-3 mb-0 italic" style={{ color: "#6B6560" }} />
        </motion.div>
        </EditableElement>

        {/* Farmstand Order Ticket buy box — whole card is one hide/delete unit */}
        <EditableElement elementKey="farmstand:order-ticket-card" toolbarSide="left" testId="element-farmstand-order-ticket">
        <motion.div style={{ marginBottom: 24, marginTop: 32 }} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.5 }}>
          <div style={{ background: "#FFFCF5", border: "2px dashed #D8CFC3", borderRadius: 12, overflow: "hidden", position: "relative", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
            {craftStatement && (
              <div style={{ position: "absolute" as const, top: 12, right: 12, zIndex: 2, maxWidth: 180, backgroundColor: "var(--color-brand-deepnavy)", color: "var(--color-brand-cream)", padding: "8px 12px", borderRadius: 6, transform: "rotate(2deg)", boxShadow: "0 3px 10px rgba(0,0,0,0.25)", fontSize: 11, fontWeight: 800, lineHeight: 1.3, textAlign: "center" as const, fontStyle: "italic" as const }} data-testid="text-farmstand-craft-statement">
                <EditableText as="span" formatOnly regenAnchor path={["quotes", "craft_statement", "quote"]} value={craftStatement} placeholder="" multiline />
              </div>
            )}
            <div className="group/lpimg relative">
              <LpImage src={orderTicketImage} alt="Product bundle" path={v2 ? "Product bundle photo" : "/bundle-flatlay.jpeg"} rounded={false} objectFit="contain" style={{ height: 240, backgroundColor: "#FAF6EE" }} />
              <LpImageActions slotKey="product:farmstand-cta" productPhotos={bundleProductPhotos} revertLabel="Use default" revertTitle="Use the default product photo" />
            </div>

            <div style={{ backgroundColor: "var(--color-brand-deepnavy)", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <EditableText as="span" formatOnly path={["originCopy", "order_ticket_label"]} value={(originCopy as any)?.order_ticket_label ?? (v2 ? "Your Order" : "Farmstand Order")} placeholder={v2 ? "Your Order" : "Farmstand Order"} style={{ color: "var(--color-brand-cream)", fontWeight: 900, fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase" as const }} />
              <span style={{ color: "rgba(244,237,221,0.5)", fontSize: 10, fontWeight: 600 }}>{brandName?.trim() || (v2 ? "Your Brand" : "Papabear Naturals")}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", padding: "0 4px", marginTop: -1 }}>
              {Array.from({ length: 20 }).map((_, i) => (
                <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "var(--color-brand-cream)", marginTop: -4 }} />
              ))}
            </div>

            <div style={{ padding: "8px 16px 0" }}>
              {r.lineItems.map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px dashed #D8CFC3" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-brand-deepnavy)" }}>{item.name}</span>
                  {item.priceLabel && <span style={{ fontSize: 13, fontWeight: 700, color: "#6B6560", textDecoration: r.hasDiscount ? "line-through" : "none" }}>{item.priceLabel}</span>}
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-brand-deepnavy)" }}>U.S. Shipping</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-brand-greenaccent)" }}>FREE</span>
              </div>
            </div>

            {r.hasDiscount && (
              <div style={{ padding: "6px 16px", borderTop: "2px solid var(--color-brand-deepnavy)", marginTop: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 1 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: "var(--color-brand-deepnavy)", letterSpacing: "0.06em", textTransform: "uppercase" as const }}>Retail Total</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#6B6560", textDecoration: "line-through" }}>{r.retailLabel}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: "var(--color-brand-greenaccent)", letterSpacing: "0.06em", textTransform: "uppercase" as const }}>{r.discountRowLabel}{r.discountPercent ? ` (${r.discountPercent}% off)` : ""}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: "var(--color-brand-greenaccent)" }}>{r.discountAmountLabel}</span>
                </div>
              </div>
            )}

            <div style={{ padding: "10px 16px", borderTop: "2px dashed #D8CFC3", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ fontSize: 14, fontWeight: 900, color: "var(--color-brand-deepnavy)", margin: 0 }}>YOU PAY TODAY</p>
                <p style={{ fontSize: 10, color: "#6B6560", margin: 0, marginTop: 2 }}>{r.guaranteeDays}-day supply{r.perDayLabel ? ` · ${r.perDayLabel}` : ""}</p>
              </div>
              <span style={{ fontSize: 32, fontWeight: 900, color: "var(--color-brand-deepnavy)" }}>{r.priceTodayLabel}</span>
            </div>

            <a href={v2 ? (checkoutUrl || undefined) : CHECKOUT_URL} style={{ display: "block", backgroundColor: "var(--color-brand-greenaccent)", padding: "14px", textAlign: "center" as const, textDecoration: "none" }}>
              <p style={{ color: "#fff", fontWeight: 900, fontSize: 15, margin: 0, letterSpacing: "0.02em" }}>{r.ctaText}</p>
              <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 10, margin: 0, marginTop: 3, fontWeight: 500 }}>Free shipping · {r.guaranteeDays}-Day money-back guarantee</p>
            </a>

            <div style={{ display: "flex", flexWrap: "wrap" as const, gap: "3px 12px", justifyContent: "center", padding: "10px 12px 0" }}>
              {r.guaranteeBullets.map((t) => (
                <div key={t} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <CheckCircle2 style={{ width: 10, height: 10, color: "var(--color-brand-greenaccent)", flexShrink: 0 }} />
                  <span style={{ fontSize: 9, fontWeight: 600, color: "#5A554F" }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
        </EditableElement>

      </div>
    </section>
  );
}
